/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import MsDyn365, { AsyncResultStatus, getUrlSync } from '@msdyn365-commerce/core';
import {
    Address,
    CardPaymentAcceptResult,
    CardType,
    CartsDataActions,
    StoreOperationsDataActions,
    TokenizedPaymentCard
} from '@msdyn365-commerce/retail-proxy';
import { withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { IModuleProps, INodeProps, Modal } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { get, isEmpty } from 'lodash';
import { action, computed, reaction, when } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { AsyncResultStatusCode, IExpressPaymentDetails } from '../../common';
import { AddPaymentFormComponent } from '../../common/add-payment-form';
import { ErrorComponent } from '../../common/error';
import { Iframe } from '../../common/iframe';
import { PaymentConnectorPostMessageType } from '../../common/payment-instrument-message';
import { WaitingComponent } from '../../common/waiting';
import getCardPaymentAcceptPointAction, { GetCardPaymentAcceptPointInput } from './actions/get-card-payment-accept-point';
import { ICheckoutExpressData } from './checkout-express.data';
import { ICheckoutExpressProps, IHeadingData } from './checkout-express.props.autogenerated';

/**
 * Interface for CheckOutExpress Module Props.
 */
export interface ICheckoutExpressModuleProps extends ICheckoutExpressProps<ICheckoutExpressData> {}

/**
 * Checkout Express State.
 */
interface ICheckoutExpressState {
    errorMessage?: string;
    isFetchingPaymentConnector?: boolean;
    paymentConnectorHeight?: number;
    isPaymentVerificationRequired?: boolean;
    paymentVerificationPostData?: string;
    isPaymentProcessing?: boolean;
    isOverlayModal?: boolean;
    terminalError?: Error;
    paymentConnectorId?: string;
    shouldHideExpressCheckout?: boolean;
}

/**
 * Checkout Express Overlay Modal.
 */
export interface ICheckoutExpressOverlayModal {
    modal: INodeProps;
}

/**
 * Checkout Express View Props.
 */
export interface ICheckoutExpressViewProps extends ICheckoutExpressProps<{}>, ICheckoutExpressState {
    className?: string;
    checkoutExpress: IModuleProps;
    waiting?: React.ReactNode;
    alert?: React.ReactNode;
    addPaymentForm?: React.ReactNode;
    overlayModal?: ICheckoutExpressOverlayModal;
    title?: React.ReactNode;
    shouldHideExpressCheckout?: boolean;
}

const zeroAmount = 0;
const expressPaymentSectionClassName = 'msc-express-payment-container';
const expressPaymentSectionInCartPageClassName = 'msc-express-payment-container-in-cart-page';
const expressPaymentSectionTextBlockClassName = 'msc-express-payment-textblock';

/**
 *
 * CheckoutExpress component.
 * @extends {React.Component<ICheckoutExpressProps<ICheckoutExpressData>>}
 */
// @ts-expect-error - existing code.
@withModuleState
@observer
export class CheckoutExpress extends React.PureComponent<ICheckoutExpressModuleProps, ICheckoutExpressState> {
    private readonly moduleClassName: string = 'ms-checkout-express';

    private readonly iframeRef: React.RefObject<Iframe> = React.createRef();

    public constructor(props: ICheckoutExpressModuleProps) {
        super(props);
        this.state = {
            isFetchingPaymentConnector: true,
            shouldHideExpressCheckout:
                (!this.isInCartPage && this.isExpressPaymentAppliedInCartPage) ||
                this.isEmptyCart ||
                this.props.data.checkout?.result?.isPaymentVerificationRedirection ||
                (!this.isInCartPage &&
                    !this.props.context.request.cookies.isConsentGiven() &&
                    this.props.context.request.user.isAuthenticated)
        };
    }

    public componentDidMount(): void {
        this.props.data.cart.result?.updateExtensionProperties({
            newExtensionProperties: [
                {
                    Key: 'expressPaymentDetails',
                    Value: { StringValue: '' }
                }
            ]
        });

        when(
            () => this.asyncResultStatus !== AsyncResultStatusCode.LOADING,
            async () => {
                if (this.props.data.checkout.result?.isPaymentVerificationRedirection) {
                    this.shouldHideExpressPaymentSectionElementByClassName(expressPaymentSectionClassName, true);
                }

                await this.init();
            }
        );

        reaction(
            () => this.props.data.checkout.result?.redirectAdditionalContext && this.props.data.checkout.result?.redirectPaymentConnectorId,
            () => {
                if (
                    this.props.data.checkout.result?.isExpressCheckoutApplied &&
                    this.props.data.checkout.result?.redirectAdditionalContext &&
                    this.props.data.cardPaymentAcceptPoint.result?.PaymentConnectorId ===
                        this.props.data.checkout.result?.redirectPaymentConnectorId
                ) {
                    this.redirectToPaymentVerification(this.props.data.checkout.result.redirectAdditionalContext);
                }
            }
        );

        reaction(
            () => this.props.data.checkout.result?.isPlaceOrderEnabledInRedirection,
            () => {
                if (
                    this.props.data.checkout.result?.isExpressCheckoutApplied &&
                    this.props.data.checkout.result?.isPlaceOrderEnabledInRedirection &&
                    this.props.data.cardPaymentAcceptPoint.result?.PaymentConnectorId ===
                        this.props.data.checkout.result?.redirectPaymentConnectorId
                ) {
                    this.init();
                }
            }
        );

        reaction(
            () => this.props.data.checkout.result?.isCheckoutCallFailed,
            () => {
                if (this.props.data.checkout.result?.isCheckoutCallFailed) {
                    this.setState({
                        isPaymentProcessing: false
                    });
                    this.shouldHideExpressPaymentSectionElementByClassName(expressPaymentSectionClassName, false);
                }
            }
        );

        reaction(
            () => this.amountDue,
            async amountDue => {
                if (this.isInCartPage && amountDue) {
                    await this.updatePaymentAcceptPageData();
                }
            }
        );

        this.shouldHideExpressPaymentSectionElementByClassName(expressPaymentSectionClassName, this.state.shouldHideExpressCheckout);
        this.shouldHideExpressPaymentSectionElementByClassName(
            expressPaymentSectionInCartPageClassName,
            this.state.shouldHideExpressCheckout
        );
    }

    public render(): JSX.Element | null {
        const {
            errorMessage,
            isFetchingPaymentConnector,
            paymentConnectorHeight,
            isPaymentProcessing,
            isOverlayModal,
            shouldHideExpressCheckout
        } = this.state;

        const {
            config: { heading, iFrameHeightOverride, paymentStyleOverride, className, paymentTenderType },
            resources,
            data: { cardPaymentAcceptPoint }
        } = this.props;

        // eslint-disable-next-line @typescript-eslint/naming-convention -- following existing naming.
        const { AcceptPageUrl, AcceptPageContent, MessageOrigin } = cardPaymentAcceptPoint.result ?? {
            AcceptPageUrl: undefined,
            AcceptPageContent: undefined,
            MessageOrigin: undefined
        };

        let paymentTenderTypeClassName = paymentTenderType ? `${this.moduleClassName}-${paymentTenderType.toLowerCase()}` : '';
        paymentTenderTypeClassName += this.isInCartPage ? '-in-cart-page' : '';

        if (typeof window !== 'undefined') {
            const classList = document.getElementsByClassName(expressPaymentSectionTextBlockClassName)[0]?.parentElement?.classList;

            classList?.forEach(classNameInList => {
                if (classNameInList.startsWith('col-md')) {
                    classList.remove(classNameInList);
                }
            });

            classList?.add('col-md-has-textblock');
        }

        const viewProps: ICheckoutExpressViewProps = {
            ...this.props,
            ...this.state,
            className,

            checkoutExpress: {
                moduleProps: this.props,
                className: classnames(this.moduleClassName, paymentTenderTypeClassName, className)
            },
            waiting: this.asyncResultStatus !== AsyncResultStatusCode.FAILED &&
                (this.asyncResultStatus === AsyncResultStatusCode.LOADING || isFetchingPaymentConnector) && (
                    <WaitingComponent {...{ message: resources.loadingMessage, className: this.moduleClassName }} />
                ),
            alert: errorMessage && (
                <ErrorComponent {...{ title: resources.errorMessageTitle, message: errorMessage, className: this.moduleClassName }} />
            ),
            addPaymentForm: !isPaymentProcessing && (AcceptPageUrl || AcceptPageContent) && (
                <AddPaymentFormComponent
                    acceptPageUrl={AcceptPageUrl}
                    acceptPageContent={AcceptPageContent}
                    messageOrigin={MessageOrigin}
                    onIFrameMessage={this.oniFrameMessage}
                    iframeRef={this.iframeRef}
                    canSubmit={false}
                    canCancel={false}
                    iFrameHeightOverride={iFrameHeightOverride ?? paymentConnectorHeight}
                    requestUrlOrigin={this.requestUrlOrigin}
                    isFetchingPaymentConnector={isFetchingPaymentConnector}
                    paymentStyleOverride={paymentStyleOverride}
                    className={this.moduleClassName}
                    moduleName='checkout-express'
                    resources={resources}
                />
            ),
            overlayModal: {
                modal: {
                    tag: Modal,
                    className: `${this.moduleClassName}__overlay-modal`,
                    isOpen: isOverlayModal
                }
            },
            title: heading && !this.isInCartPage && this.renderMsdyn365Text(heading),
            shouldHideExpressCheckout: shouldHideExpressCheckout ?? this.isEmptyCart
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Renders the heading for the module.
     * @param heading - The heading data.
     * @returns The react node.
     */
    public renderMsdyn365Text(heading: IHeadingData): React.ReactNode {
        return (
            <Msdyn365.Text
                {...heading}
                tag={heading.tag ?? 'h2'}
                className='checkout-express__title'
                text={heading.text}
                editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
            />
        );
    }

    /**
     * Handles the heading change event.
     * @param event - The event.
     */
    public handleHeadingChange(event: Msdyn365.ContentEditableEvent): void {
        this.props.config.heading!.text = event.target.value;
    }

    /**
     * Handle payment result.
     * @param resultAccessCode - Result access code.
     */
    @action
    private readonly handlePaymentResult = async (
        resultAccessCode: string,
        isRedirectedFromPaymentGateway: boolean = false
    ): Promise<void> => {
        const {
            data: { checkout, cardPaymentAcceptPoint },
            context: {
                request: {
                    user: { isAuthenticated, signInUrl }
                }
            },
            config: { paymentTenderType }
        } = this.props;

        const cartId = checkout.result?.checkoutCart.cart.Id ?? '';
        const paymentConnectorId = cardPaymentAcceptPoint.result?.PaymentConnectorId;

        const settings = {
            ReturnUrl: this.getReturnUrl(),
            ...(paymentConnectorId && { PaymentConnectorId: paymentConnectorId })
        };

        if (isRedirectedFromPaymentGateway) {
            const cardPaymentAcceptResult: CardPaymentAcceptResult = {
                AccessCode: resultAccessCode,
                Settings: settings
            };

            checkout?.result?.updateCardPaymentAcceptResult({ newCardPaymentAcceptResult: cardPaymentAcceptResult }).then(async () => {
                this.props.data.checkout.result?.checkoutCart.refreshCart({});

                await checkout.result?.updateShouldCollapsePaymentSection({ newShouldCollapsePaymentSection: true });
                await checkout.result?.updateIsPaymentSectionContainerReady({ newIsPaymentSectionContainerReady: true });
            });

            return;
        } else if (checkout.result?.shippingAddressFromExpressCheckout) {
            const cardPaymentAcceptResult: CardPaymentAcceptResult = {
                AccessCode: resultAccessCode,
                Settings: settings
            };

            const tokenizedPaymentCard: TokenizedPaymentCard = {
                CardTypeId: paymentTenderType
            };

            this.setState({
                terminalError: undefined,
                errorMessage: undefined,
                shouldHideExpressCheckout: true
            });

            if (this.isInCartPage) {
                const address = checkout.result?.shippingAddressFromExpressCheckout;

                const expressPaymentDetails: IExpressPaymentDetails = {
                    paymentTenderType,
                    tokenizedPaymentCard,
                    address,
                    isExpressCheckoutAppliedInCartPage: true,
                    isExpressAddressAppliedInCartPage: true,
                    cardPaymentAcceptResult
                };

                const newExtensionProperties = [
                    {
                        Key: 'expressPaymentDetails',
                        Value: { StringValue: JSON.stringify(expressPaymentDetails) }
                    }
                ];

                this.props.data.cart.result?.updateExtensionProperties({ newExtensionProperties });

                this.shouldHideExpressPaymentSectionElementByClassName(expressPaymentSectionInCartPageClassName, true);

                checkout.result?.updateIsExpressCheckoutAppliedInCartPage({ newIsExpressCheckoutAppliedInCartPage: true });

                const guestCheckoutUrl = getUrlSync('checkout', this.props.context.actionContext) ?? '';
                const checkoutUrl = this.getCheckoutUrl(isAuthenticated, guestCheckoutUrl, signInUrl ?? '');

                if (MsDyn365.isBrowser) {
                    window.location.href = escape(isAuthenticated ? checkoutUrl : guestCheckoutUrl);
                }
            } else {
                checkout.result
                    ?.updateCardPaymentAcceptResult({ newCardPaymentAcceptResult: cardPaymentAcceptResult })
                    .catch((error: Error) => {
                        this.setTerminalError(error);
                    });

                checkout.result?.updateExpressCheckoutDetails({
                    newPaymentTenderType: paymentTenderType,
                    newIsExpressCheckoutApplied: true,
                    newIsExpressAddressApplied: true,
                    newCardPaymentAcceptResult: checkout.result.cardPaymentAcceptResult,
                    newEmailAddressFromExpressCheckout: checkout.result.emailAddressFromExpressCheckout,
                    newTenderLine: checkout.result.tenderLine,
                    newTokenizedPaymentCard: tokenizedPaymentCard
                });

                this.shouldHideExpressPaymentSectionElementByClassName(expressPaymentSectionClassName, true);
            }

            return;
        }

        CartsDataActions.retrieveCardPaymentAcceptResultAsync(
            { callerContext: this.props.context.actionContext },
            resultAccessCode,
            [],
            cartId,
            settings
        )
            .then(async cardPaymentAcceptResult => {
                // eslint-disable-next-line @typescript-eslint/naming-convention, @typescript-eslint/no-shadow -- Public name.
                const { TenderLine, TokenizedPaymentCard, Email } = cardPaymentAcceptResult;

                if (!TokenizedPaymentCard) {
                    this.setTerminalError(new Error('No TokenizedPaymentCard found'));
                    return;
                }
                const checkoutState = this.props.data.checkout.result;
                if (checkoutState) {
                    // Try to update card prefix, if it is not set already.
                    const cardPrefix = TokenizedPaymentCard.CardTokenInfo?.MaskedCardNumber;
                    if (cardPrefix) {
                        await checkoutState.updateCardPrefix({ newCardPrefix: cardPrefix });
                    }

                    if (!TokenizedPaymentCard.CardTypeId) {
                        const cardTypeId = await this.getCardTypeId(checkoutState.cardPrefix ?? '');
                        if (!cardTypeId) {
                            const error = new Error('The specified card type is not supported.');
                            error.name = 'CARDTYPENOTFOUND';
                            this.setTerminalError(error);
                            return;
                        }

                        TokenizedPaymentCard.CardTypeId = cardTypeId;
                    }

                    if (this.isInCartPage) {
                        const address = this.getAddressFromTokenizedPaymentCard(TokenizedPaymentCard);

                        const expressPaymentDetails: IExpressPaymentDetails = {
                            email: Email,
                            tenderLine: TenderLine,
                            tokenizedPaymentCard: TokenizedPaymentCard,
                            paymentTenderType,
                            address,
                            isExpressCheckoutAppliedInCartPage: true
                        };

                        const newExtensionProperties = [
                            {
                                Key: 'expressPaymentDetails',
                                Value: { StringValue: JSON.stringify(expressPaymentDetails) }
                            }
                        ];

                        this.props.data.cart.result?.updateExtensionProperties({ newExtensionProperties });

                        this.shouldHideExpressPaymentSectionElementByClassName(expressPaymentSectionInCartPageClassName, true);

                        await checkoutState.updateIsExpressCheckoutAppliedInCartPage({ newIsExpressCheckoutAppliedInCartPage: true });
                    } else {
                        await checkoutState.updateExpressCheckoutDetails({
                            newEmailAddressFromExpressCheckout: Email ?? '',
                            newTenderLine: TenderLine,
                            newTokenizedPaymentCard: TokenizedPaymentCard,
                            newPaymentTenderType: paymentTenderType,
                            newIsExpressCheckoutApplied: true,
                            newIsExpressAddressApplied: false,
                            newCardPaymentAcceptResult: checkoutState.cardPaymentAcceptResult
                        });

                        await this.saveShippingAndBillingAddress(TokenizedPaymentCard);

                        this.shouldHideExpressPaymentSectionElementByClassName(expressPaymentSectionClassName, true);
                    }

                    this.setState({
                        terminalError: undefined,
                        errorMessage: undefined,
                        shouldHideExpressCheckout: true
                    });
                }
            })
            .catch(async (error: Error) => {
                // IMPORTANT: Call RS for getting a new payment session. It should not re-use the previous session for the security reason.
                await this.updatePaymentAcceptPageData();
                this.setTerminalError(error);
                this.setState({ shouldHideExpressCheckout: false });

                this.shouldHideExpressPaymentSectionElementByClassName(expressPaymentSectionClassName, false);
                this.shouldHideExpressPaymentSectionElementByClassName(expressPaymentSectionInCartPageClassName, false);
            })
            .finally(() => {
                this.setState({
                    isPaymentProcessing: false
                });

                if (this.isInCartPage) {
                    const guestCheckoutUrl = getUrlSync('checkout', this.props.context.actionContext) ?? '';
                    const checkoutUrl = this.getCheckoutUrl(isAuthenticated, guestCheckoutUrl, signInUrl ?? '');

                    if (MsDyn365.isBrowser) {
                        window.location.href = escape(isAuthenticated ? checkoutUrl : guestCheckoutUrl);
                    }
                }
            });
    };

    /**
     * Init.
     */
    @action
    private readonly init = async (): Promise<void> => {
        const {
            data: { checkout, cart },
            config
        } = this.props;

        if (checkout.result?.isPaymentVerificationRedirection && checkout.result?.isPlaceOrderEnabledInRedirection) {
            if (config.paymentTenderType === checkout.result?.paymentTenderType) {
                const { requestFormData, query } = this.props.context.request;

                const formData = btoa(JSON.stringify(requestFormData ?? { redirectResult: query?.redirectResult }));

                this.setState({
                    isPaymentProcessing: true
                });
                this.handlePaymentResult(formData, true);
            }
        } else if (this.asyncResultStatus === AsyncResultStatusCode.FAILED) {
            this.setState({
                isFetchingPaymentConnector: false
            });

            if (this.isInCartPage && ArrayExtensions.hasElements(cart.result?.cart.CartLines)) {
                await this.updatePaymentAcceptPageData();
            } else {
                this.setTerminalError(new Error('Failed in load data'));
            }
        }
    };

    /**
     * Set terminal error.
     * @param terminalError - The terminal error.
     */
    @action
    private readonly setTerminalError = (terminalError: Error): void => {
        this.props.telemetry.exception(terminalError);
        const {
            resources: { genericErrorMessage, cardTypeErrorMessage }
        } = this.props;
        let errorMessage = genericErrorMessage;

        if (terminalError.name === 'CARDTYPENOTFOUND') {
            errorMessage = cardTypeErrorMessage;
        }

        this.setState({
            terminalError,
            errorMessage
        });
    };

    /**
     * Set error message.
     * @param errorMessage - The error message.
     */
    @action
    private readonly setErrorMessage = (errorMessage: string): void => {
        this.props.telemetry.error(errorMessage);
        this.setState({
            errorMessage
        });
    };

    @computed public get isEmptyCart(): boolean {
        if (this.isEditorialMode) {
            // Editorial mode: Ignore empty cart
            return false;
        }

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined..
        const cart = this.props.data.cart?.result?.cart;
        return !!cart && !isEmpty(cart) && (!cart.CartLines || !ArrayExtensions.hasElements(cart.CartLines));
    }

    @computed public get isEditorialMode(): boolean {
        return this.props.context.request.params.isEditor;
    }

    @computed public get amountDue(): number {
        const {
            data: { cart, checkout }
        } = this.props;
        let currentCart;

        if (this.isInCartPage) {
            if (!cart.result) {
                return 0;
            }

            currentCart = cart.result.cart;
        } else {
            if (!checkout.result) {
                return 0;
            }

            const checkoutResult = checkout.result;
            currentCart = checkoutResult.checkoutCart.cart;
        }

        if (!currentCart.CartLines || !ArrayExtensions.hasElements(currentCart.CartLines)) {
            return 0;
        }

        // Use the express checkout for all amount.
        return currentCart.TotalAmount ?? zeroAmount;
    }

    @computed public get asyncResultStatus(): AsyncResultStatus | undefined {
        const isLoading = Object.values(this.props.data).some(data => get(data, 'status') === AsyncResultStatusCode.LOADING);
        if (isLoading) {
            return AsyncResultStatusCode.LOADING;
        }

        const isSuccess = Object.values(this.props.data).every(data => get(data, 'status') === AsyncResultStatusCode.SUCCESS);
        if (isSuccess) {
            return AsyncResultStatusCode.SUCCESS;
        }

        const isFailed = Object.values(this.props.data).some(data => get(data, 'status') === AsyncResultStatusCode.FAILED);
        if (isFailed) {
            return AsyncResultStatusCode.FAILED;
        }

        return undefined;
    }

    @computed public get requestUrlOrigin(): string {
        const origin = window.location.origin;
        const propsRequestUrl = this.props.context.request.url.requestUrl;
        const requestUrl = typeof propsRequestUrl === 'string' ? new URL(propsRequestUrl) : propsRequestUrl;

        return origin || requestUrl.origin;
    }

    public get isInCartPage(): boolean {
        const cartUrlSync = getUrlSync('cart', this.props.context.actionContext);
        let cartUrl = cartUrlSync || '/cart';
        cartUrl = cartUrl.includes('?') ? cartUrl.substring(0, cartUrl.indexOf('?')) : cartUrl;
        return this.props.context.request.canonicalUrl?.toLocaleLowerCase().includes(cartUrl) ?? false;
    }

    public get expressPaymentDetailsFromCartPage(): IExpressPaymentDetails | null {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
        const properties =
            this.props.data.cart?.result?.cart?.ExtensionProperties?.find(property => property.Key === 'expressPaymentDetails')?.Value
                ?.StringValue ?? '';

        return properties ? (JSON.parse(properties) as IExpressPaymentDetails) : null;
    }

    public get isExpressPaymentAppliedInCartPage(): boolean {
        return this.expressPaymentDetailsFromCartPage?.isExpressCheckoutAppliedInCartPage ?? false;
    }

    /**
     * Should hide the express payment section.
     * @param name - The class name of express payment section.
     * @param shouldHide - Should hide the section or not.
     */
    private readonly shouldHideExpressPaymentSectionElementByClassName = (name: string, shouldHide?: boolean): void => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- explicit null/undefined check.
        const expressPaymentTextElements = document?.getElementsByClassName(name);

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- explicit null/undefined check.
        if (expressPaymentTextElements?.[0]) {
            const expressPaymentTextElement = expressPaymentTextElements[0] as HTMLInputElement;
            expressPaymentTextElement.style.display = shouldHide ? 'none' : '';
        }
    };

    /**
     * On iFrame has message.
     * @param event - Message event.
     */
    private readonly oniFrameMessage = (event: MessageEvent): void => {
        let result;
        try {
            if (typeof event.data !== 'string') {
                return;
            }
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Event is any.
            result = JSON.parse(event.data);
        } catch (error) {
            this.setTerminalError(error as Error);
            return;
        }

        const paymentConnectorId = this.props.data.cardPaymentAcceptPoint.result?.PaymentConnectorId;

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Jason parse result is any.
        if (!result || !result.type || result.id !== paymentConnectorId) {
            if (result.type && !result.id) {
                this.props.telemetry.error('Connector id is not sent from the connector.');
                console.error(`${this.props.config.paymentTenderType} express connector id is not sent from the connector.`);
            }

            return;
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Jason parse result is any.
        const { type, value, express = false } = result;

        // If express flag in the result is false, then express module should not handle this message.
        if (type !== PaymentConnectorPostMessageType.Height && !express) {
            return;
        }

        this.props.telemetry.information(
            `Express payment instrument ${this.props.config.paymentTenderType ?? ''} handles onIframeMessage ${type}.`
        );

        switch (type) {
            case PaymentConnectorPostMessageType.Height:
                // The payment connector will post height after it completes initialization
                if (this.state.isFetchingPaymentConnector) {
                    this.setState({
                        isFetchingPaymentConnector: false,
                        paymentConnectorHeight: Number(value)
                    });

                    if (this.props.data.cardPaymentAcceptPoint?.result && this.props.data.checkout?.result) {
                        const extension = Array.from(this.props.data.checkout.result?.isExpressPaymentModulesReady ?? []);
                        const newIsExpressPaymentModulesReady = [
                            ...extension,
                            {
                                Key: `is${this.props.config.paymentTenderType}ExpressModuleReady`,
                                Value: { BooleanValue: true }
                            }
                        ];

                        this.props.data.checkout.result?.updateIsExpressPaymentModulesReady({ newIsExpressPaymentModulesReady });
                    }
                }
                return;
            case PaymentConnectorPostMessageType.Result:
                this.handlePaymentResult(String(value));
                return;
            case PaymentConnectorPostMessageType.ShippingAddress:
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- Jason parse result is any.
                this.handleShippingAddress(value);
                return;
            case PaymentConnectorPostMessageType.Error:
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- Jason parse result is any.
                this.handlePaymentError(value);
                return;
            case PaymentConnectorPostMessageType.Showoverlay:
                this.showOverlayModal(true);
                return;
            case PaymentConnectorPostMessageType.Hideoverlay:
                this.showOverlayModal(false);
                break;

            default:

            // Do nothing
        }
    };

    /**
     * Show overlay modal.
     * @param isShow - Is show.
     */
    private readonly showOverlayModal = (isShow: boolean): void => {
        this.setState({ isOverlayModal: isShow });
    };

    /**
     * Get card type id.
     * @param cardPrefix - Card prefix.
     * @returns Card type id.
     */
    private readonly getCardTypeId = async (cardPrefix: string = ''): Promise<string | undefined> => {
        try {
            const response = await StoreOperationsDataActions.resolveCardTypesAsync(
                { callerContext: this.props.context.actionContext },
                cardPrefix,
                CardType.Unknown
            );
            if (ArrayExtensions.hasElements(response)) {
                return response[0].TypeId;
            }
        } catch (error) {
            this.props.context.telemetry.error('Call to resolveCardTypesAsync failed.', error);
        }
        return undefined;
    };

    private readonly redirectToPaymentVerification = async (paymentVerificationPostData: string): Promise<void> => {
        if (!paymentVerificationPostData) {
            return;
        }
        const checkoutState = this.props.data.checkout.result;
        if (checkoutState) {
            await checkoutState.updatePaymentTenderType({ newPaymentTenderType: this.props.config.paymentTenderType });
            await checkoutState.saveDataInStorage({});
            await checkoutState.updateIsPaymentVerificationRequired({ newIsPaymentVerificationRequired: true });
        }
        const redirectInfo = JSON.parse(paymentVerificationPostData);
        const { url, data } = redirectInfo;
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = url;
        for (const propertyName of Object.keys(data)) {
            const element = document.createElement('input');
            element.name = propertyName;
            element.value = data[propertyName];
            form.appendChild(element);
        }
        document.body.appendChild(form);
        form.submit();
    };

    /**
     * Handle payment error.
     * @param value - Message.
     */
    private readonly handlePaymentError = (value?: { message: string }[]): void => {
        const {
            resources: { defaultSubmitErrorMessage = 'An error occurred in payment method details. Please try again.' }
        } = this.props;
        if (!value || !ArrayExtensions.hasElements(value)) {
            // Fallback, if no message could be translated from payment accept page response
            this.setErrorMessage(defaultSubmitErrorMessage);
            return;
        }
        this.setErrorMessage(value.map((errorValue: { message: string }) => errorValue.message).join('\n'));
    };

    private readonly getReturnUrl = (): string => {
        let returnUrl = getUrlSync('checkout', this.props.context.actionContext) || '';
        const absoluteUrlRegExp = new RegExp('^(?:[a-z]+:)?//', 'i');
        const isAbsoluteUrl = absoluteUrlRegExp.test(returnUrl);
        if (window && !isAbsoluteUrl) {
            returnUrl = `${window.location.origin}${returnUrl}`;
        }
        return `${returnUrl}${!returnUrl.includes('?') ? '?' : '&'}pv=1`;
    };

    /**
     * Handle shipping address.
     * @param value - Message.
     */
    private readonly handleShippingAddress = async (value?: string): Promise<void> => {
        if (value) {
            const parsedValue = JSON.parse(value);
            const address: Address = {
                TwoLetterISORegionName: parsedValue.countryCode,
                Name: parsedValue.name,
                Street: parsedValue.address1,
                StreetNumber: parsedValue.address2,
                City: parsedValue.locality,
                State: parsedValue.administrativeArea,
                ZipCode: parsedValue.postalCode
            };
            await this.props.data.checkout.result?.updateBillingAddressFromExpressCheckout({
                newBillingAddressFromExpressCheckout: address
            });
            await this.props.data.checkout.result?.updateShippingAddressFromExpressCheckout({
                newShippingAddressFromExpressCheckout: address
            });
        }
    };

    /**
     * Update payment accept page data.
     */
    private readonly updatePaymentAcceptPageData = async (): Promise<void> => {
        const {
            context: { actionContext },
            config: { paymentTenderType },
            context: {
                request: { apiSettings }
            },
            data: { checkout }
        } = this.props;

        const input = {
            paymentTenderType,
            apiSettings,
            shouldEnableSinglePaymentAuthorizationCheckout: checkout.result?.shouldEnableSinglePaymentAuthorizationCheckout,
            amountDue: this.amountDue
        };

        this.setState({
            isFetchingPaymentConnector: true
        });

        const checkoutState = checkout.result;
        if (checkoutState) {
            await checkoutState.updatePaymentTenderType({ newPaymentTenderType: undefined });
            await checkoutState.updateTenderLine({ newTenderLine: undefined });
            await checkoutState.updateTokenizedPaymentCard({ newTokenizedPaymentCard: undefined });
        }

        getCardPaymentAcceptPointAction(new GetCardPaymentAcceptPointInput(input), actionContext)
            .then(paymentAcceptPoint => {
                // In case, add form is not hidden. We need to explicity reset value to force re-rendering.
                actionContext.update(new GetCardPaymentAcceptPointInput(input), { AcceptPageContent: '', AcceptPageUrl: '' });

                // IMPORTANT: Do NOT set isFetchingPaymentConnector = false in getCardPaymentAcceptPointAction success response.
                // The payment connector will post a message with content height when payment is ready.
                actionContext.update(new GetCardPaymentAcceptPointInput(input), paymentAcceptPoint);
                this.setState({ paymentConnectorId: paymentAcceptPoint.PaymentConnectorId });

                return paymentAcceptPoint;
            })
            .catch((error: Error) => {
                this.setTerminalError(error);

                this.setState({
                    isFetchingPaymentConnector: false
                });

                // IMPORTANT: Clear the AcceptPageUrl and AcceptPageContent. It should not re-use the previous session for the security reason.
                actionContext.update(new GetCardPaymentAcceptPointInput(input), { AcceptPageContent: '', AcceptPageUrl: '' });
            });
    };

    /**
     * Save the address as Shipping and Billing address.
     * @param tokenizedPaymentCard - Tokenized payment card.
     */
    private readonly saveShippingAndBillingAddress = async (tokenizedPaymentCard?: TokenizedPaymentCard): Promise<void> => {
        const checkoutState = this.props.data.checkout.result;

        // For Paypal, the address returned in TokenizedPaymentCard will be used as both shipping and billing address.
        if (checkoutState && tokenizedPaymentCard && tokenizedPaymentCard.Zip) {
            const address: Address = this.getAddressFromTokenizedPaymentCard(tokenizedPaymentCard);

            await checkoutState.updateBillingAddressFromExpressCheckout({ newBillingAddressFromExpressCheckout: address });
            await checkoutState.updateShippingAddressFromExpressCheckout({ newShippingAddressFromExpressCheckout: address });
        }
    };

    /**
     * Get address from tokenizedPaymentCard.
     * @param tokenizedPaymentCard -- The tokenizedPaymentCard from the payment.
     * @returns The address.
     */
    private readonly getAddressFromTokenizedPaymentCard = (tokenizedPaymentCard: TokenizedPaymentCard): Address => {
        const address: Address = {
            TwoLetterISORegionName: tokenizedPaymentCard.Country,
            Name: tokenizedPaymentCard.NameOnCard,
            Street: tokenizedPaymentCard.Address1,
            StreetNumber: tokenizedPaymentCard.Address2,
            City: tokenizedPaymentCard.City,
            State: tokenizedPaymentCard.State,
            ZipCode: tokenizedPaymentCard.Zip,
            Phone: tokenizedPaymentCard.Phone
        };

        return address;
    };

    /**
     * Gets the checkout URL.
     * @param isAuthenticated - Is authenticated flag.
     * @param guestCheckoutUrl - Guest checkout URL.
     * @param signInUrl -The sign in URL.
     * @returns The checkout URL.
     */
    private readonly getCheckoutUrl = (isAuthenticated: boolean, guestCheckoutUrl: string, signInUrl: string): string => {
        if (isAuthenticated) {
            return guestCheckoutUrl;
        }

        const absoluteUrlRegExp = new RegExp('^(?:[a-z]+:)?//', 'i');
        const isAbsoluteUrl = absoluteUrlRegExp.test(guestCheckoutUrl);
        let returnUrl = guestCheckoutUrl;
        if (MsDyn365.isBrowser && !isAbsoluteUrl) {
            returnUrl = `${window.location.origin}${guestCheckoutUrl}`;
        }

        return `${signInUrl}${!signInUrl.includes('?') ? '?' : '&'}ru=${returnUrl}`;
    };
}

export default CheckoutExpress;
